import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@douyinfe/semi-ui';
import { useNavigate } from 'react-router-dom';
import { Image, Modal } from '@/components';
import { LocalStorage } from '@/utils';
import './DashboardIntroDialog.scss';
import introIllustrationFirst from '@/assets/media/quickStart/dashboard-intro-1.png';
import introIllustrationSecond from '@/assets/media/quickStart/dashboard-intro-2.png';
import { BILLING_TABS, getBillingPath } from '@/config/billing';

type IntroDialogSlideCopy = {
  title: string;
  description: string;
  primaryAction: string;
  secondaryAction?: string;
  illustrationAlt: string;
};

type IntroDialogCopy = {
  learnMore: string;
  documentationSuffix: string;
  slides: {
    overview: IntroDialogSlideCopy;
    credits: IntroDialogSlideCopy;
  };
};

const CREDIT_DOCS_URL = 'https://docs.chainbase.com/resources/platform/credit/overview';
const DASHBOARD_INTRO_DISMISSED_KEY = 'DashboardIntroDialogDismissed';

export default function DashboardIntroDialog(): React.ReactElement | null {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(() => !LocalStorage.get<boolean>(DASHBOARD_INTRO_DISMISSED_KEY, false));
  const [activeStep, setActiveStep] = React.useState(0);

  const introI18n = t('dashboard.intro', { returnObjects: true }) as IntroDialogCopy;
  const slides = [
    {
      ...introI18n.slides.overview,
      illustration: introIllustrationFirst
    },
    {
      ...introI18n.slides.credits,
      illustration: introIllustrationSecond
    }
  ];
  const currentSlide = slides[activeStep];

  const dismissDialog = (): void => {
    LocalStorage.set(DASHBOARD_INTRO_DISMISSED_KEY, true);
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  const handlePrimaryAction = (): void => {
    if (activeStep === 0) {
      setActiveStep(1);
      return;
    }

    dismissDialog();
    navigate(getBillingPath(BILLING_TABS.credit));
  };

  return (
    <Modal
      className="dashboard-intro-dialog"
      centered
      closable={false}
      visible={visible}
      width={836}
      footer={null}
      maskClosable={false}
      closeOnEsc={false}
    >
      <div className="dashboard-intro-dialog__container">
        <div className="dashboard-intro-dialog__header">
          <h2 className="dashboard-intro-dialog__title">{currentSlide.title}</h2>
        </div>

        <div className="dashboard-intro-dialog__divider" />

        <div className="dashboard-intro-dialog__body">
          <div className="dashboard-intro-dialog__copy-column">
            <p className="dashboard-intro-dialog__description">{currentSlide.description}</p>
          </div>

          <div className="dashboard-intro-dialog__visual-column">
            <div className="dashboard-intro-dialog__visual-frame">
              <Image
                className="dashboard-intro-dialog__illustration"
                src={currentSlide.illustration}
                alt={currentSlide.illustrationAlt}
              />
            </div>
          </div>
        </div>

        <div className="dashboard-intro-dialog__pagination" aria-label="Dialog pagination">
          {slides.map((slide, index) => (
            <button
              key={slide.primaryAction}
              type="button"
              className={`dashboard-intro-dialog__dot${index === activeStep ? ' dashboard-intro-dialog__dot--active' : ''}`}
              onClick={() => setActiveStep(index)}
              aria-label={`Go to step ${index + 1}`}
              aria-pressed={index === activeStep}
            />
          ))}
        </div>

        <div className="dashboard-intro-dialog__footer">
          <button
            type="button"
            className="dashboard-intro-dialog__learn-more"
            onClick={() => window.open(CREDIT_DOCS_URL, '_blank', 'noreferrer')}
          >
            <span className="dashboard-intro-dialog__learn-more-link">{introI18n.learnMore}</span>
            <span>{introI18n.documentationSuffix}</span>
          </button>

          <div className="dashboard-intro-dialog__actions">
            {currentSlide.secondaryAction ? (
              <Button
                className="dashboard-intro-dialog__button dashboard-intro-dialog__button--secondary"
                theme="solid"
                onClick={dismissDialog}
                htmlType="button"
              >
                <span>{currentSlide.secondaryAction}</span>
              </Button>
            ) : null}

            <Button
              className={`dashboard-intro-dialog__button dashboard-intro-dialog__button--primary${activeStep === 0 ? ' dashboard-intro-dialog__button--next' : ''}`}
              theme="solid"
              onClick={handlePrimaryAction}
              htmlType="button"
            >
              <span>{currentSlide.primaryAction}</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
